<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
//import DateInput from '../ui/form/DateInput.vue';
import { api } from '../../api/Api';
import { formatDate, formatNumber, isNumeric } from '../../utils/CommonFunctions';
import Buttons from '../ui/form/Buttons.vue';
import ModalFormTemplate from '../ui/Templates/ModalFormTemplate.vue';
import Toast from '../../services/Toast';
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import "jspdf-autotable";

// Filter states
const fromDate = ref<Date | string>('');
const toDate = ref<Date | string>('');
const selectedOutlet = ref(null);

// Dialog state
const showTransactions = ref(false);
const selectedTransactions = ref<any[]>([]);
const dialogTitle = ref('');

// Show transaction history
const showTransactionHistory = (account: ProfitLoss) => {
  dialogTitle.value = account.account_name;
  getTransactions(account);
};
const columns = ref<Array<{ field: keyof ProfitLoss; header: string }>>([
  { field: "account_name", header: "Account Name" },
  { field: "debit", header: "Debit" },
  { field: "credit", header: "Credit" },
]);

const exportToExcel = () => {
  // Create worksheet with proper data types
  const ws: XLSX.WorkSheet = {};
  const range = {
    s: { c: 0, r: 0 },
    e: { c: columns.value.length - 1, r: reportData.value.length },
  };

    console.log("Exporting to Excel:", {
    columns: columns.value,
    data: reportData.value,
  });

  // Add headers
  columns.value.forEach((col, colIdx) => {
    const cell: XLSX.CellObject = {
      v: col.header,
      t: "s", // string type for headers
    };
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIdx });
    ws[cellRef] = cell;
  });

  // Add data with proper types
  reportData.value.forEach((row, rowIdx) => {
    columns.value.forEach((col, colIdx) => {
      const cellRef = XLSX.utils.encode_cell({ r: rowIdx + 1, c: colIdx });
      const rawValue = row[col.field];

      let cell: XLSX.CellObject;

      // Normalize and cast values to acceptable CellObject types
      if (typeof rawValue === "number" || isNumeric(rawValue as any)) {
        // ensure the value is a number (coerce strings that are numeric)
        cell = { v: Number(rawValue), t: "n" }; // number type
      } else if (typeof rawValue === "boolean") {
        cell = { v: rawValue, t: "b" }; // boolean type
      } else if (rawValue instanceof Date) {
        cell = { v: rawValue, t: "d" }; // date type
      } else {
        // Fallback to string; handle null/objects safely by stringifying or empty string
        const strVal = rawValue != null && typeof rawValue !== "object" ? String(rawValue) : (rawValue != null ? JSON.stringify(rawValue) : "");
        cell = { v: strVal, t: "s" }; // string type
      }

      ws[cellRef] = cell;
    });
  });

  ws["!ref"] = XLSX.utils.encode_range(range);

  // Create workbook and export
  const wb = XLSX.utils.book_new();
  let fileName = "Trial Balance as at ";
  if (fileName.length > 30) {
    fileName = fileName.substring(0, 30);
  }
  XLSX.utils.book_append_sheet(wb, ws, fileName);
  // Use only the first 30 characters of the title if it exceeds 30

  XLSX.writeFile(wb, "Trial Balance as at " + formatDate(new Date()) + ".xlsx");
};

const exportToPDF = async (forExport: boolean = false) => {
  // Set orientation to landscape
  const doc = new jsPDF({ orientation: "landscape" });
  const headers = columns.value.map((col) => col.header);
  const data = reportData.value.map((row) =>
    columns.value.map((col) => {
      const value = row[col.field];
      if (isNumeric(value)) {
        return Number(value).toFixed(2);
      }
      return String(value);
    })
  );

  (doc as any).autoTable({
    head: [headers],
    body: data,
    margin: { top: 20 },
    styles: { fontSize: 8 },
    headStyles: { fillColor: [76, 175, 80] },
  });

  if (forExport) {
    if (navigator.share) {
      try {
        const pdfBlob = doc.output("blob");
        const file = new File([pdfBlob], "Trial Balance.pdf", {
          type: "application/pdf",
        });

        await navigator.share({
          title: "Faseyha POS",
          text: "Trial Balance",
          files: [file],
        });

        //console.log("PDF shared successfully!");
      } catch (error) {
        Toast.show("Could not share", "warning");
      }
    }
  } else {
    doc.save("Trial Balance as at " + formatDate(new Date()) + ".pdf");
  }
};

const getTransactions = async (account: ProfitLoss) => {
  try {
    const response = await api.post("/clients/reports/transactions", {
      from_date: fromDate.value,
      to_date: toDate.value,
      chart_of_accounts_id: account.chart_of_accounts_id,
    });
    if (response.success == true) {
      selectedTransactions.value = response.data;
      showTransactions.value = true;
    }
  } catch (error) {
    Toast.show(String(error) || "Failed to fetch transactions", 'error');
  }
}

const getData = async () => {
  try {
    currentPage.value = 1;
    const response = await api.post("/clients/reports/trial-balance", {
      from_date: fromDate.value,
      to_date: toDate.value,
      outlet: selectedOutlet.value
    });
    if (response.success == true) {
      reportData.value = response.data;
    }
  } catch (error) {
    Toast.show(String(error) || "Failed to fetch Data", 'error');
  }
}

interface ProfitLoss {
  account_name: string;
  debit: number;
  credit: number;
  account_type: string;
  chart_of_accounts_id: number | null;
  indent: number;
  is_category: number;
  is_subtotal: number;
  parent: string | null;
  sort_order: number;
  monthly?: Record<string, number>;
}
const reportData = ref<ProfitLoss[]>([]);

onMounted(() => {
  // Initialize date range to the last date of the current year
  const today = new Date();
  fromDate.value = formatDate(new Date(today.getFullYear(), 0, 1));
  toDate.value = formatDate(new Date(today.getFullYear(), 11, 31));
  getData();
});

const pageSize = 25;
const currentPage = ref(1);

const totalPages = computed(() => Math.ceil(selectedTransactions.value.length / pageSize));

const paginatedTransactions = computed(() => {
  const start = (currentPage.value - 1) * pageSize;
  return selectedTransactions.value.slice(start, start + pageSize);
});

function prevPage() {
  if (currentPage.value > 1) currentPage.value--;
}
function nextPage() {
  if (currentPage.value < totalPages.value) currentPage.value++;
}



</script>

<template>
  <div class="profit-loss">
    <h1 class="report-title">Trial Balance</h1>
    
    <!-- Filters -->
    <!--<div class="filters">
      <div class="filter-group">
          <DateInput 
            v-model="fromDate" 
            :showIcon="true"
            dateFormat="yyy-mm-dd"
            label="From Date"
          />
      </div>
      
      <div class="filter-group">
        <DateInput 
            v-model="toDate" 
            :showIcon="true"
            dateFormat="yyy-mm-dd"
            label="To Date"
          />
      </div>

      <div class="filter-actions">
        <div>
        <Buttons caption="Generate" icon="RotateCw" @click="getData" />
        </div>
      </div>
    </div>-->
    <div class="report-container">
    <table class="styled-ledger-table w-full">
  <thead>
    <tr>
      <th class="section-header"></th>
      <th class="text-right section-header" colspan="2">
        <div style="display:flex; justify-content:flex-end; gap:0.5rem; align-items:center;">
          <Buttons caption="Export to Excel" icon="Sheet" @click="exportToExcel" />
          <Buttons caption="Export to PDF" icon="FileText" @click="exportToPDF" />
        </div>
      </th>
    </tr>
    <tr>
      <th class="section-header">Account Name</th>
      <th class="text-right section-header">Debit</th>
      <th class="text-right section-header">Credit</th>
    </tr>
  </thead>

  <tbody>
    <tr v-for="(d, index) in reportData" :key="index">
      <td><span @dblclick="showTransactionHistory(d)" class="cursor-pointer item-label">{{ d.account_name }}</span></td>
      <td class="text-right item-label">{{ formatNumber(d.debit) }}</td>
      <td class="text-right item-label">{{ formatNumber(d.credit) }}</td>
    </tr>
  </tbody>

  <tfoot>
    <tr>
      <th>Total</th>
      <th class="text-right">
        {{ formatNumber(reportData.reduce((sum, item) => Number(sum) + (Number(item.debit) || 0), 0)) }}
      </th>
      <th class="text-right">
        {{ formatNumber(reportData.reduce((sum, item) => Number(sum) + (Number(item.credit) || 0), 0)) }}
      </th>
    </tr>
  </tfoot>
</table>
    </div>
    

    <!-- Transaction History Dialog -->
    <ModalFormTemplate :mode="'view'" :title="dialogTitle + ' - Transaction History'" end-point=""
    v-if="showTransactions" :ignore-permission="true"
    @close="showTransactions = false">
    <template #form>
      <div class="transaction-history">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th class="text-right" style="text-align: right;">Debit</th>
              <th class="text-right" style="text-align: right;">Credit</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(transaction, index) in paginatedTransactions" :key="index">
              <td>{{ formatDate(transaction.date) }}</td>
              <td>{{ transaction.record_type }}</td>
              <td class="amount">{{ formatNumber(transaction.debit_amount) }}</td>
              <td class="amount">{{ formatNumber(transaction.credit_amount) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="2" style="text-align: right; font-weight: bold;">Total:</td>
              <td class="amount" style="font-weight: bold;">{{ formatNumber(selectedTransactions.reduce((sum, t) => sum + Number(t.debit_amount), 0)) }}</td>
              <td class="amount" style="font-weight: bold;">{{ formatNumber(selectedTransactions.reduce((sum, t) => sum + Number(t.credit_amount), 0)) }}</td>
            </tr>
          </tfoot>
        </table>
        <div class="pagination" style="display: flex; align-items: center; justify-content: center; gap: 1rem;" v-if="totalPages > 1">
          <Buttons caption="Previous" icon="ChevronLeft" @click="prevPage" :disabled="currentPage === 1" />          
          <span>Page {{ currentPage }} of {{ totalPages }}</span>
          <Buttons caption="Next" icon="ChevronRight" @click="nextPage" :disabled="currentPage === totalPages" />
        </div>
            </div>
      </template>
      </ModalFormTemplate>

  </div>
</template>

<style scoped>
.profit-loss {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

.report-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #1a5fb4;
  text-align: center;
  margin-bottom: 2rem;
}

.filters {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
}

.filter-group label {
  font-size: 0.875rem;
  color: #495057;
  font-weight: 500;
}

.outlet-dropdown {
  width: 100%;
}

.calendar-container {
  width: 100%;
}

.calendar-container :deep(.p-calendar) {
  width: 100%;
}

.calendar-container :deep(.p-datepicker) {
  min-width: 350px;
}

.calendar-container :deep(.calendar-footer) {
  display: flex;
  justify-content: flex-end;
  padding: 0.5rem;
  border-top: 1px solid #dee2e6;
}

.filter-actions {
  display: flex;
  align-items: flex-end;
}

.report-container {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 2rem;
}

.section {
  margin-bottom: 2rem;
}

.section-header {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a5fb4;
  border-bottom: 2px solid #1a5fb4;
  padding-bottom: 0.2rem;
  margin-bottom: 1rem;
  display: table-cell;
}

.line-item {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  font-size: 0.8rem;
  margin-left: 1rem;
  cursor: zoom-in;
}

.item-label {
  color: #495057;
}

.line-item span:last-child {
  font-family: monospace;
}

.subtotal {
  display: flex;
  justify-content: space-between;
  margin-left: 1rem;
  padding: 0.75rem 0;
  font-weight: 600;
  border-top: 1px solid #dee2e6;
  border-bottom: 2px solid #353636;
  margin-top: 0.5rem;
  margin-bottom: 2rem;
}

.major-total {
  display: flex;
  justify-content: space-between;
  padding: 1rem 0;
  font-weight: 700;
  font-size: 1.1rem;
  color: #1a5fb4;
  border-top: 2px solid #1a5fb4;
  border-bottom: 2px solid #1a5fb4;
  margin: 2rem 0;
  background-color: #f8f9fa;
}

.net-profit {
  color: #2ec27e;
  border-color: #2ec27e;
  font-size: 1.25rem;
}

.transaction-history table {
  width: 100%;
  border-collapse: collapse;
}

.transaction-history th,
.transaction-history td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid #dee2e6;
}

.transaction-history th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #495057;
}

.transaction-history .amount {
  text-align: right;
  font-family: monospace;
}

@media (max-width: 768px) {
  .profit-loss {
    padding: 1rem;
  }

  .filters {
    padding: 1rem;
    gap: 1rem;
  }

  .report-container {
    padding: 1rem;
  }

  .line-item {
    margin-left: 0.5rem;
  }
}
</style>